import { installedItems } from "@p0security/shared/install/installed";
import type { CsaIntegration } from "@p0security/shared/integrations/resources/csa/types";
import { Typography } from "antd";
import type { FrontendInstallContext } from "install/types";
import type { ReactNode } from "react";

import type { CsaInstaller } from "./types";

type CsaInstructionParams = {
  /** Human-readable summary of what this integration grants P0 */
  accessDescription: string;
  /** List of scopes to select when creating the API key */
  scopes: ReactNode;
};

const csaInstallInstructions =
  ({ accessDescription, scopes }: CsaInstructionParams) =>
  (_context: FrontendInstallContext<CsaIntegration>, _id: string) => ({
    help: (
      <>
        <Typography.Paragraph>{accessDescription}</Typography.Paragraph>
        <Typography.Paragraph strong>
          Create an API key in Cisco Secure Access
        </Typography.Paragraph>
        <Typography.Paragraph>
          In the Cisco Secure Access admin console, navigate to{" "}
          <Typography.Text code>Admin</Typography.Text> &rarr;{" "}
          <Typography.Text code>Management</Typography.Text> &rarr;{" "}
          <Typography.Text code>API Keys</Typography.Text>. Click{" "}
          <Typography.Text code>Add</Typography.Text> and enter an{" "}
          <Typography.Text code>API Key Name</Typography.Text>. Select the
          following scopes:
        </Typography.Paragraph>
        {scopes}
        <Typography.Paragraph>
          Choose an expiry for the API key. You will have to refresh the key
          before the expiry.
        </Typography.Paragraph>
      </>
    ) as ReactNode,
  });

export const csaInstaller: CsaInstaller = {
  "iam-write": {
    instructions: csaInstallInstructions({
      accessDescription:
        "Complete the following steps to grant P0 full access to manage Cisco Secure Access policies.",
      scopes: (
        <ul>
          <li>
            <Typography.Text code>Policies / Private Resources</Typography.Text>{" "}
            &rarr; Read-Only
          </li>
          <li>
            <Typography.Text code>Policies / Private Resources</Typography.Text>{" "}
            &rarr; Read / Write
          </li>
          <li>
            <Typography.Text code>Reports</Typography.Text> &rarr;{" "}
            <Typography.Text code>Utilities</Typography.Text> &rarr; Read-Only
          </li>
        </ul>
      ),
    }),
    optionProvider: async (context) => {
      const existingIamWrite = installedItems("iam-write", context.config);
      const networkAccessItems = installedItems(
        "network-access",
        context.config
      );
      return Object.entries(networkAccessItems)
        .filter(([id, item]) => !!item && !(id in existingIamWrite))
        .map(([id, item]) => ({ id, label: item?.label ?? id }));
    },
    items: {},
  },
  "network-access": {
    instructions: csaInstallInstructions({
      accessDescription:
        "Complete the following steps to grant P0 read-only access to your Cisco Secure Access private resources.",
      scopes: (
        <ul>
          <li>
            <Typography.Text code>Policies / Private Resources</Typography.Text>{" "}
            &rarr; Read-Only
          </li>
        </ul>
      ),
    }),
    items: {},
  },
};
